/**
 * StatusBadge — badge trạng thái chuẩn (timber).
 * Caller quyết định tone/label; component không đổi nghĩa nghiệp vụ.
 */
import { Badge, BADGE_DOT_CLASS, type BadgeTone } from './Badge'
import type { ProductStatus } from '@/services/products'
import { statusLabel } from '@/services/products'

export type { BadgeTone }

type GenericProps = {
  tone: BadgeTone
  label: string
  showDot?: boolean
  className?: string
  title?: string
  /** Discriminator — không dùng cùng lúc với status */
  status?: never
}

type ProductCompatProps = {
  status: ProductStatus
  tone?: never
  label?: never
  showDot?: boolean
  className?: string
  title?: string
}

export type StatusBadgeProps = GenericProps | ProductCompatProps

const productTone: Record<ProductStatus, BadgeTone> = {
  ok: 'success',
  pending: 'warning',
  inactive: 'neutral',
  discontinued: 'danger',
}

export function productStatusTone(status: ProductStatus): BadgeTone {
  return productTone[status]
}

export function StatusBadge(props: StatusBadgeProps) {
  if ('status' in props && props.status != null) {
    const { status, showDot = true, className = '', title } = props
    return (
      <StatusBadgeInner
        tone={productStatusTone(status)}
        label={statusLabel(status)}
        showDot={showDot}
        className={className}
        title={title}
      />
    )
  }

  const {
    tone,
    label,
    showDot = true,
    className = '',
    title,
  } = props as GenericProps
  return (
    <StatusBadgeInner
      tone={tone}
      label={label}
      showDot={showDot}
      className={className}
      title={title}
    />
  )
}

function StatusBadgeInner({
  tone,
  label,
  showDot,
  className,
  title,
}: {
  tone: BadgeTone
  label: string
  showDot: boolean
  className: string
  title?: string
}) {
  return (
    <Badge tone={tone} className={className} title={title}>
      {showDot ? (
        <span
          className={`inline-block h-1.5 w-1.5 rounded-full ${BADGE_DOT_CLASS[tone]}`}
          aria-hidden
        />
      ) : null}
      {label}
    </Badge>
  )
}

/** Alias rõ ràng cho Product — cùng mapping tone như StatusBadge({ status }). */
export function ProductStatusBadge({ status }: { status: ProductStatus }) {
  return <StatusBadge status={status} />
}
